import {flattenDepth} from 'lodash';
import polylabel from 'polylabel';
import {BoundsObject, MultiPolygonCoords, PointCoords, PolygonCoords} from '@/map/@types';
import {getArrayDepth} from '@/utils';

export const retouchPoint = (point: number) => {
  // 네이버 지도 좌표는 소수점 이하 8자리에서 반올림 하여, 소수점 이하 7자리 까지 표현하도록 변환됨
  // https://developers.naver.com/forum/posts/7942
  const toFix = 10_000_000;

  return Math.round(point * toFix) / toFix;
};

export const convertToCoordFromPointCoords = (
  coordinates: PointCoords | number[]
): naver.maps.Coord | null => {
  if (!coordinates?.length) {
    return null;
  }

  return new naver.maps.LatLng(
    retouchPoint(coordinates[1]),
    retouchPoint(coordinates[0])
  );
};

export const getPolygonPaths = (paths: naver.maps.PolygonOptions['paths']) => {
  const path: naver.maps.Point[] = [];

  new naver.maps.Polygon({paths}).getPath().forEach((i) => {
    path.push(i);
  });

  return path;
};

export const getPolygonCoords = (
  paths: naver.maps.PolygonOptions['paths']
): PolygonCoords => {
  return [getPolygonPaths(paths).map((i) => [i.x, i.y])];
};

export const flattenPolygonCoords = (
  coordinates: PolygonCoords | MultiPolygonCoords
): PointCoords[] => {
  const depth = getArrayDepth(coordinates);
  if (depth < 2) {
    return coordinates as any;
  }
  return flattenDepth(coordinates, depth - 2) as any;
};

export const flattenPolygonToNaverCoords = (
  coordinates: PolygonCoords | MultiPolygonCoords
): naver.maps.Coord[] => {
  return (flattenPolygonCoords(coordinates) as any).map((i: PointCoords) =>
    convertToCoordFromPointCoords(i)
  );
};

export const convertBoundsToBoundsObject = (
  bounds?: naver.maps.Bounds
): BoundsObject | undefined => {
  if (!bounds) {
    return undefined;
  }

  return {
    lat1: bounds.minY(),
    lat2: bounds.maxY(),
    lng1: bounds.minX(),
    lng2: bounds.maxX(),
  };
};

export const getMaxPoygon = (polygons: naver.maps.ArrayOfCoords[]) => {
  const maxIndex = polygons.reduce(
    (acc: number, i: naver.maps.ArrayOfCoords, idx: number) => {
      const areaSize = new naver.maps.Polygon({paths: [i]}).getAreaSize();
      const maxSize = new naver.maps.Polygon({
        paths: [polygons[acc]],
      }).getAreaSize();

      if (areaSize > maxSize) {
        return idx;
      }

      return acc;
    },
    0
  );

  return {
    index: maxIndex,
    polygon: polygons[maxIndex],
  };
};

export const getGeometryCenterPoint = (geometry: MultiPolygonCoords) => {
  if (!geometry) {
    return;
  }

  const polygons = convertToPolygonPathsFromGeometry(geometry);
  const {index} = getMaxPoygon(polygons);

  const [x, y] = polylabel(geometry[index], 1.0);

  if (x && y) {
    return convertToCoordFromPointCoords([x, y]);
  }
};

export const convertToPolygonPathsFromGeometry = (
  geometry: PolygonCoords | MultiPolygonCoords
): naver.maps.ArrayOfCoords[] => {
  const res: any = geometry.map((i) => {
    if (getArrayDepth(i) === 1) {
      return convertToCoordFromPointCoords(i as any);
    } else {
      return convertToPolygonPathsFromGeometry(i as any);
    }
  });

  return res;
};
